import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Formik, Form } from 'formik';

import { FormServiceContext } from 'app/utils/contexts';
import { makeStyles, Grid, Tabs, Tab, CircularProgress } from '@material-ui/core';

import Request from './components/Request';
import Documentation from 'app/components/Documentation';
import AlertMessage from 'app/components/AlertMessage';
import RegistrationStatus from '../../Registration/components/RegistrationStatus';
import SaveButton from 'app/components/SaveButton';
import FinalizeButton from 'app/components/FinalizeButton';
import SearchRequest from './components/SearchRequest';
import notification from 'app/config/notification';

import licenseEndpoint from 'app/api/services/licenseEndpoint';
import requestEndpoints from 'app/api/services/requestEndpoints';

import { LICENSE_CHAGES_TYPE, SERVICES_CODES } from 'app/config/services';
import { fetchCities } from 'app/store/slices/forms';
import { STATE_CITY } from 'app/utils/constants';

import {
    fetchVehicleTypes,
    fetchVehicleCategories,
    fetchInspectedVehicles,
    fetchAccessibilityType,
    fetchVehicleModels,
    fetchVehicleBrands,
    fetchFuelType,
    fetchVehicleClassification,
    fetchSubSystems,
    fetchInspectedVehiclesByDocument,
    fetchLicenseVehicleDocuments,
} from 'app/store/slices/vehicle';

import styles from './styles';
import { fetchServiceDocuments } from 'app/store/slices/services';
import {
    licensesEventualValidationSchema,
    licensesTourismValidationSchema,
    licensesValidationSchema,
} from 'app/utils/forms/validationsSchemas';
const useStyles = makeStyles(styles);

function LicenseDuplicateRegistration() {
    const classes = useStyles();
    const dispatch = useDispatch();

    const contextProps = useContext(FormServiceContext);

    const {
        initialValues,
        validateForm,
        onSubmit,
        onSave,
        onCancel,
        mainColor,
        hideActionBtn,
        hideFinalizeBtn,
        canEdit,
        isInternalForm,
        loadingDocuments,
    } = contextProps;

    const viewIdentification = 0;
    const viewDocumentation = 1;

    const [loading, setLoading] = useState(true);
    const [changingDate, setChangingDate] = useState(0);
    const [view, setView] = useState(0);
    const [validationSchema, setValidationSchema] = useState(null);

    const [oldRequest, setOldRequest] = useState(null);
    const [oldLicense, setOldLicense] = useState(null);
    const [oldService, setOldService] = useState({});
    const [previousRequestCode, setPreviousRequestCode] = useState(null);

    const [selectedChangeType, setSelectedChangeType] = useState(null);
    const [formInitialValues, setFormInitialValues] = useState({});

    const inspectedVehicles = useSelector((state) => state.vehicle.inspectedVehicles);
    const cities = useSelector((state) => state.forms.cities);
    const vehicleType = useSelector((state) => state.vehicle.vehicleType);
    const vehicleCategories = useSelector((state) => state.vehicle.vehicleCategories);
    const accessibilityType = useSelector((state) => state.vehicle.accessibilityType);
    const fuelType = useSelector((state) => state.vehicle.fuelType);
    const vehicleClassification = useSelector((state) => state.vehicle.vehicleClassification);
    const vehicleModels = useSelector((state) => state.vehicle.vehicleModels);
    const vehicleBrands = useSelector((state) => state.vehicle.vehicleBrands);
    const subSystems = useSelector((state) => state.vehicle.subSystems);
    const documentsStore = useSelector((state) => state.services.documents);
    const internalNewRequest = useSelector((state) => state.forms.internalNewRequest);

    const request = useSelector((state) => state.requests.details);
    const requestJsonData = useMemo(() => {
        return request.jsonSolicitacao ? JSON.parse(request.jsonSolicitacao) : {};
    }, [request]);

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            const promisses = [];
            let document = requestJsonData.deCpfCnpj || request.cdUsuarioSolicitacao;
            if (internalNewRequest && internalNewRequest.company) {
                document =
                    internalNewRequest.company.deCnpjMatriz ||
                    internalNewRequest.company.deCpfCnpj ||
                    internalNewRequest.cdUsuario;
                promisses.push(dispatch(fetchInspectedVehiclesByDocument(document)));
            } else if (isInternalForm) {
                promisses.push(dispatch(fetchInspectedVehiclesByDocument(document)));
            } else {
                if (inspectedVehicles.length === 0) {
                    promisses.push(dispatch(fetchInspectedVehicles()));
                }
            }

            if (cities.length === 0) promisses.push(dispatch(fetchCities(STATE_CITY)));
            if (vehicleType.length === 0) promisses.push(dispatch(fetchVehicleTypes()));
            if (vehicleCategories.length === 0) promisses.push(dispatch(fetchVehicleCategories()));
            if (accessibilityType.length === 0) promisses.push(dispatch(fetchAccessibilityType()));
            if (vehicleModels.length === 0) promisses.push(dispatch(fetchVehicleModels()));
            if (vehicleBrands.length === 0) promisses.push(dispatch(fetchVehicleBrands()));
            if (fuelType.length === 0) promisses.push(dispatch(fetchFuelType()));
            if (vehicleClassification.length === 0) promisses.push(dispatch(fetchVehicleClassification()));
            if (subSystems.length === 0) promisses.push(dispatch(fetchSubSystems()));
            if (promisses.length > 0) await Promise.all(promisses);
            setLoading(false);
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const handleViewChange = (event, value) => {
        setView(value);
    };

    const onChangeDate = (values, field, value) => {
        setChangingDate(changingDate + 1);
        values[field] = value;
    };

    const handleSelectRequest = useCallback(
        async (requestListParam, changeType, fromInit) => {
            setLoading(true);
            let oldRequestParam = null;
            if (!fromInit) {
                //buscando solicitação anterior para trabalhar com ela
                const solicitacaoAnterior = requestListParam.cdSolicitacao;
                setPreviousRequestCode(solicitacaoAnterior);
                const response = await requestEndpoints.findRequest({
                    cdSolicitacao: solicitacaoAnterior,
                });
                if (response.status !== 200 || !response.data) {
                    setLoading(false);
                    return;
                }
                oldRequestParam = response.data;
            } else {
                oldRequestParam = requestListParam;
            }

            setOldRequest(oldRequestParam);
            setSelectedChangeType(changeType);
            let tempOldLicense = JSON.parse(oldRequestParam.jsonSolicitacao);
            let tempOldService = oldRequestParam.servico || {};
            if (!fromInit) {
                try {
                    const originRequestCode = requestListParam.cdSolicitacaoOrigem || requestListParam.cdSolicitacao;
                    if (originRequestCode) {
                        const cdLicencaOrigem = tempOldLicense.cdLicenca;
                        const response = await licenseEndpoint.getNewLicenseDuplicate({
                            cdLicencaOrigem,
                            cdSolicitacaoOrigem: originRequestCode,
                        });
                        if (response.status === 200 && response.data) {
                            const resp = response.data;
                            // tempOldLicense = JSON.parse(resp.jsonSolicitacao);
                            tempOldService = resp.servico;
                        }
                    }
                } catch (error) {
                    console.error(error);
                }
                setOldService(tempOldService);
            }
            setOldLicense(tempOldLicense);

            const initValues = { ...initialValues };

            initValues.nuMeses = String(tempOldLicense.nuMeses || '');
            initValues.frequencyTest = null;
            initValues.boMatutino = tempOldLicense.boMatutino || false;
            initValues.boVespertino = tempOldLicense.boVespertino || false;
            initValues.boNoturno = tempOldLicense.boNoturno || false;

            setFormInitialValues(initValues);

            setLoading(false);
        },
        [initialValues],
    );

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            if (request.jsonSolicitacao) {
                const jsonSolicitacao = JSON.parse(request.jsonSolicitacao);
                if (jsonSolicitacao?.cdSolicitacaoOrigem) {
                    const response = await requestEndpoints.findRequest({
                        cdSolicitacao: jsonSolicitacao.cdSolicitacaoOrigem,
                    });
                    if (response.status === 200 && response.data) {
                        const req = response.data;
                        if (req.servico) {
                            setOldService(req.servico);
                        }
                    }
                }
            }
            setLoading(false);
        };
        init();
    }, [request.jsonSolicitacao]);

    useEffect(() => {
        const init = async () => {
            const cdServico = oldService.cdServico;
            if (documentsStore.length === 0 && cdServico) {
                await dispatch(fetchServiceDocuments({ cdServico: cdServico }));
                if (oldService.cdServico) {
                    await dispatch(fetchLicenseVehicleDocuments(oldService.cdServico));
                }
            }
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, oldService]);

    useEffect(() => {
        const init = async () => {
            if (Number(selectedChangeType) === LICENSE_CHAGES_TYPE.MUDANCA_CONTRATO) {
                if (
                    oldService.cdServico === SERVICES_CODES.LICENCA_FRETAMENTO_UNIVERSITARIOS ||
                    oldService.cdServico === SERVICES_CODES.LICENCA_FRETAMENTO
                ) {
                    setValidationSchema(licensesValidationSchema);
                } else if (oldService.cdServico === SERVICES_CODES.LICENCA_TURISMO) {
                    setValidationSchema(licensesTourismValidationSchema);
                } else if (oldService.cdServico === SERVICES_CODES.LICENCA_VIAGEM_EVENTUAL) {
                    setValidationSchema(licensesEventualValidationSchema);
                }
            }
        };
        init();
    }, [dispatch, oldService, selectedChangeType]);

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            if (request?.cdServico && request.cdServico === SERVICES_CODES.LICENCA_SEGUNDA_VIA) {
                const cdSolicitacao = requestJsonData?.cdSolicitacaoAnterior
                    ? requestJsonData.cdSolicitacaoAnterior
                    : requestJsonData?.cdSolicitacaoOrigem;
                if (cdSolicitacao) {
                    const response = await requestEndpoints.findRequest({
                        cdSolicitacao: cdSolicitacao,
                    });
                    if (response.status === 200 && response.data) {
                        await handleSelectRequest(response.data, requestJsonData?.cdTipoAlteracaoLicenca, true);
                    }
                }
            }
            setLoading(false);
        };
        init();
    }, [handleSelectRequest, request, requestJsonData]);

    const renderTabs = () => {
        return (
            <Grid container spacing={3} className={classes.tabsContainer}>
                <Tabs
                    variant="scrollable"
                    onChange={handleViewChange}
                    className={classes.tabs}
                    indicatorColor={mainColor ? mainColor : 'primary'}
                    textColor={mainColor ? mainColor : 'primary'}
                    value={view}>
                    <Tab className={classes.tab} label={<FormattedMessage id="LABELS.SERVICES.REQUEST" />} />
                    {loadingDocuments && !isInternalForm && (
                        <Tab
                            className={classes.tab}
                            label={<CircularProgress size={12} className={classes.loading} />}
                        />
                    )}
                    {!isInternalForm && !loadingDocuments && (
                        <Tab className={classes.tab} label={<FormattedMessage id="LABELS.COMMONS.DOCUMENTATION" />} />
                    )}
                </Tabs>
            </Grid>
        );
    };

    if (loading) {
        return (
            <Grid item xs={12} className={classes.centerProgress}>
                <CircularProgress size={18} />
            </Grid>
        );
    }

    return (
        <>
            <Formik
                enableReinitialize={true}
                initialValues={formInitialValues}
                validateOnBlur={false}
                validateOnChange={false}
                validationSchema={validationSchema}
                onSubmit={(values) => {
                    onSubmit(values, {
                        oldRequest: oldRequest,
                        oldService: oldService,
                    });
                }}>
                {(props) => {
                    return (
                        <Form className={classes.containerForm}>
                            {!oldRequest && (
                                <SearchRequest
                                    {...props}
                                    contextProps={contextProps}
                                    handleSelectRequest={handleSelectRequest}
                                />
                            )}

                            {oldRequest && selectedChangeType && (
                                <>
                                    {renderTabs()}

                                    <AlertMessage isInternalForm={isInternalForm} />

                                    <Grid container spacing={3} className={classes.containerMainData}>
                                        <RegistrationStatus {...props} classes={classes} />
                                    </Grid>

                                    {view === viewIdentification && (
                                        <Request
                                            {...props}
                                            contextProps={contextProps}
                                            oldRequest={oldRequest}
                                            oldService={oldService}
                                            oldLicense={oldLicense}
                                            selectedChangeType={selectedChangeType}
                                            previousRequestCode={previousRequestCode}
                                            onChangeDate={onChangeDate}
                                        />
                                    )}
                                    {view === viewDocumentation && <Documentation {...props} />}

                                    <SaveButton
                                        mainColor={mainColor}
                                        hideActionBtn={hideActionBtn}
                                        isInternalForm={isInternalForm}
                                        canEdit={canEdit}
                                        onCancel={onCancel}
                                        onSave={() => {
                                            props.validateForm().then((res) => {
                                                if (Object.keys(res).length <= 0) {
                                                    onSave(props.values, false, isInternalForm, {
                                                        oldRequest: oldRequest,
                                                        oldService: oldService,
                                                    });
                                                } else {
                                                    notification('O(s) campo(s) sinalizado(s) precisam de revisão.', {
                                                        toastId: 'VALIDATION',
                                                        type: 'error',
                                                    });
                                                }
                                            });
                                        }}
                                    />

                                    <FinalizeButton
                                        mainColor={mainColor}
                                        hideFinalizeBtn={hideFinalizeBtn}
                                        isInternalForm={isInternalForm}
                                        canEdit={canEdit}
                                        onSubmit={() => {
                                            validateForm && validateForm(props.validateForm());
                                        }}
                                    />
                                </>
                            )}
                        </Form>
                    );
                }}
            </Formik>
        </>
    );
}

export default injectIntl(LicenseDuplicateRegistration);
